import React, { useEffect, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';

function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      const response = await api.get('/admin/invoices');
      const data = response.data.data || response.data;
      console.log('Invoices loaded:', data);
      setInvoices(data);
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast.error('Failed to load invoices: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleView = (invoice) => {
    setSelectedInvoice(invoice);
    setShowViewModal(true);
  };

  const handleDownloadPDF = async (invoiceId) => {
    try {
      const response = await api.get(`/admin/invoices/${invoiceId}/download`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const handleMarkAsPaid = async (invoiceId) => {
    if (window.confirm('Mark this invoice as paid?')) {
      try {
        await api.put(`/admin/invoices/${invoiceId}`, { status: 'paid', paid_date: new Date().toISOString().split('T')[0] });
        toast.success('Invoice marked as paid');
        loadInvoices();
      } catch (error) {
        toast.error('Failed to update invoice');
      }
    }
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">📄 Invoices</h1>
        <div className="flex gap-3">
          <button 
            onClick={loadInvoices}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-semibold"
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clinic Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {invoices.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                  <div className="text-6xl mb-4">📄</div>
                  <p>No invoices found. Invoices will be generated automatically when clinics are created.</p>
                </td>
              </tr>
            ) : (
              invoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-mono">{invoice.invoice_number}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{invoice.tenant?.clinic_name || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{formatDate(invoice.invoice_date)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{formatDate(invoice.due_date)}</td>
                  <td className="px-6 py-4 whitespace-nowrap font-semibold text-green-600">
                    ₹{parseFloat(invoice.total_amount).toLocaleString('en-IN')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                      invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button 
                      onClick={() => handleView(invoice)}
                      className="text-blue-600 hover:text-blue-800 mr-3 font-medium"
                    >
                      👁️ View
                    </button>
                    <button 
                      onClick={() => handleDownloadPDF(invoice.id)}
                      className="text-green-600 hover:text-green-800 mr-3 font-medium"
                    >
                      ⬇️ PDF
                    </button>
                    {invoice.status !== 'paid' && (
                      <button 
                        onClick={() => handleMarkAsPaid(invoice.id)}
                        className="text-purple-600 hover:text-purple-800 font-medium"
                      >
                        ✅ Mark Paid
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* View Invoice Modal */}
      {showViewModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">📄 Invoice Details</h2>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>
            
            <div className="border-b pb-6 mb-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold">Clinic ERP Solutions</h3>
                  <p className="text-gray-600 mt-2">123, Business Park</p>
                  <p className="text-gray-600">Mumbai, Maharashtra - 400001</p>
                  <p className="text-gray-600">GSTIN: 27AABCU9603R1ZM</p>
                </div>
                <div className="text-right">
                  <h4 className="text-lg font-bold">INVOICE</h4>
                  <p className="text-gray-600 mt-2">#{selectedInvoice.invoice_number}</p>
                  <p className="text-gray-600">Date: {formatDate(selectedInvoice.invoice_date)}</p>
                  <p className="text-gray-600">Due: {formatDate(selectedInvoice.due_date)}</p>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h4 className="font-semibold mb-2">Bill To:</h4>
              <p className="font-medium">{selectedInvoice.tenant?.clinic_name}</p>
              <p className="text-gray-600">{selectedInvoice.tenant?.address}</p>
              <p className="text-gray-600">{selectedInvoice.tenant?.city}, {selectedInvoice.tenant?.state} - {selectedInvoice.tenant?.pincode}</p>
              {selectedInvoice.tenant?.gstin && (
                <p className="text-gray-600">GSTIN: {selectedInvoice.tenant?.gstin}</p>
              )}
            </div>

            <table className="w-full mb-6">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Description</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t">
                  <td className="px-4 py-3">
                    {selectedInvoice.subscription?.subscription_plan?.name || 'N/A'} Subscription
                    <br />
                    <span className="text-sm text-gray-600">
                      Period: {formatDate(selectedInvoice.subscription?.start_date)} to {formatDate(selectedInvoice.subscription?.end_date)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">₹{parseFloat(selectedInvoice.subtotal).toLocaleString('en-IN')}</td>
                </tr>
              </tbody>
            </table>

            <div className="border-t pt-4">
              <div className="flex justify-between py-2">
                <span>Subtotal:</span>
                <span>₹{parseFloat(selectedInvoice.subtotal).toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between py-2">
                <span>Tax (GST 18%):</span>
                <span>₹{parseFloat(selectedInvoice.tax_amount).toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between py-3 border-t font-bold text-lg">
                <span>Total:</span>
                <span className="text-green-600">₹{parseFloat(selectedInvoice.total_amount).toLocaleString('en-IN')}</span>
              </div>
            </div>

            <div className="mt-6 bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Status:</strong> <span className={`${
                  selectedInvoice.status === 'paid' ? 'text-green-600' :
                  selectedInvoice.status === 'pending' ? 'text-yellow-600' :
                  'text-red-600'
                }`}>{selectedInvoice.status.toUpperCase()}</span>
              </p>
              {selectedInvoice.paid_date && (
                <p className="text-sm text-gray-600 mt-1">
                  <strong>Paid Date:</strong> {formatDate(selectedInvoice.paid_date)}
                </p>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => handleDownloadPDF(selectedInvoice.id)}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-semibold"
              >
                ⬇️ Download PDF
              </button>
              <button
                onClick={() => setShowViewModal(false)}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 rounded-lg font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Invoices;
